// M7.3 — Zones editor. Top-down 2D canvas for terrain painting + 4 entity
// overlays (spawn points, portals, mob spawns, npc spawns) + a small iso
// preview matching the in-game projection. CRUD: edit / create / delete
// zones with anti-orphan-portal handled server-side via cross-ref check.
//
// Live changes affect the publish draft only; the runtime-mutated registry
// kicks in at the next server restart for already-instantiated zones (v1).

import {
  DECOR_CATEGORY_COLOR,
  DECOR_KINDS,
  DECOR_SPRITES,
  type DecorPlacement,
  decorCategoryOf,
  TILE_H,
  TILE_W,
  type TerrainId,
  type ZoneJson,
} from "@vinyl/shared";
import { createCombobox } from "./combobox.js";
import { el } from "./dom.js";

export interface ZonesScreenCallbacks {
  getKnownMobIds: () => string[];
  getKnownNpcIds: () => string[];
  onDirtyChange: (dirty: boolean) => void;
}

export interface ZonesScreen {
  root: HTMLElement;
  setZones(zones: readonly ZoneJson[]): void;
  getDraft(): ZoneJson[];
  isDirty(): boolean;
  markPublished(): void;
}

interface DraftZone extends ZoneJson {
  __isNew?: boolean;
  __isDirty?: boolean;
}

const TERRAINS: TerrainId[] = ["grass", "stone", "wood", "ice", "water", "path"];
const TERRAIN_COLOR: Record<TerrainId, string> = {
  grass: "#5da14a",
  stone: "#8e8d85",
  wood: "#7a4f29",
  ice: "#a9d4e5",
  water: "#3a6fc0",
  path: "#b8895f",
};
const TERRAIN_LABEL: Record<TerrainId, string> = {
  grass: "Grass",
  stone: "Stone",
  wood: "Wood",
  ice: "Ice",
  water: "Water",
  path: "Path",
};
// Mirrors shared/src/data/zones.ts terrainBlocks: grass + ice + path walkable.
const NON_BLOCKING: ReadonlySet<TerrainId> = new Set<TerrainId>(["grass", "ice", "path"]);

type EditMode =
  | "paint"
  | "select"
  | "paste"
  | "place_sp"
  | "place_portal"
  | "place_mob"
  | "place_npc"
  | "erase";
type PaintTool = "brush" | "rect" | "bucket";

// M11 — terrain-only clipboard. Filled when a select drag completes;
// persists across zone switches so you can copy from zone A and paste
// to zone B. Cleared only when the page reloads. M11.5 will wrap this
// into named, persisted blueprints (separate publish pipe).
interface TerrainClipboard {
  width: number;
  height: number;
  terrain: TerrainId[];
}

const ZONE_MIN_SIZE = 8;
// M11 — bumped from 64 to match shared ZoneJsonSchema's new ceiling.
const ZONE_MAX_SIZE = 1024;
// M11 — max tiles painted in a single canvas pane. Above this, the
// editor switches to region-mode (mini-map navigation + visible window).
// 64 × 16 px = 1024 px wide canvas — fits a 1080p screen without scroll
// once you account for sidebar + toolbar. Earlier 128 produced 2048 px
// which forced scroll in both axes; reduced to 64 after playtest.
const REGION_SIZE = 64;
// Snap mini-map panning to multiples of this (in tiles). Same value as
// REGION_SIZE means adjacent regions are perfectly disjoint — each click
// is a clean "page" change with no overlap. Drop to 32 if you ever
// want chevauchant regions for continuity painting.
const REGION_PAN_STEP = 64;

function ix(width: number, x: number, y: number): number {
  return y * width + x;
}

function newBlankZone(id: string): DraftZone {
  const w = 24;
  const h = 24;
  const terrain: TerrainId[] = new Array(w * h).fill("grass");
  // Stone border so the player can't walk off-edge by default.
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) {
      if (x === 0 || y === 0 || x === w - 1 || y === h - 1) {
        terrain[ix(w, x, y)] = "stone";
      }
    }
  }
  return {
    id,
    displayName: id,
    width: w,
    height: h,
    isSafe: false,
    terrain,
    spawnPoints: [{ x: Math.floor(w / 2), y: Math.floor(h / 2), index: 0 }],
    portals: [],
    mobSpawns: [],
    npcSpawns: [],
    decor: [],
    __isNew: true,
    __isDirty: true,
  };
}

function sanitize(z: DraftZone): ZoneJson {
  const out: ZoneJson = {
    id: z.id,
    width: z.width,
    height: z.height,
    isSafe: z.isSafe,
    terrain: z.terrain.slice(),
    spawnPoints: z.spawnPoints.map((s) => ({ ...s })),
    portals: z.portals.map((p) => ({ ...p })),
    mobSpawns: z.mobSpawns.map((m) => ({ ...m })),
    npcSpawns: z.npcSpawns.map((n) => ({ ...n })),
  };
  if (z.displayName !== undefined && z.displayName.trim() !== "") {
    out.displayName = z.displayName.trim();
  }
  // M14 — only include decor in the payload when non-empty, to keep
  // pre-decor zones serializing identically to their disk form.
  if (z.decor && z.decor.length > 0) {
    out.decor = z.decor.map((d) => ({ ...d }));
  }
  return out;
}

function cloneZone(z: ZoneJson): DraftZone {
  return {
    id: z.id,
    ...(z.displayName !== undefined ? { displayName: z.displayName } : {}),
    width: z.width,
    height: z.height,
    isSafe: z.isSafe,
    terrain: z.terrain.slice(),
    spawnPoints: z.spawnPoints.map((s) => ({ ...s })),
    portals: z.portals.map((p) => ({ ...p })),
    mobSpawns: z.mobSpawns.map((m) => ({ ...m })),
    npcSpawns: z.npcSpawns.map((n) => ({ ...n })),
    decor: (z.decor ?? []).map((d) => ({ ...d })),
    __isNew: false,
    __isDirty: false,
  };
}

export function createZonesScreen(cb: ZonesScreenCallbacks): ZonesScreen {
  let drafts: DraftZone[] = [];
  let selectedId: string | null = null;

  // Edit-state (per-screen, not per-zone — survives zone-switching).
  // M14 — subTab persists across zone switches so the user can stay in
  // "decor" mode while jumping between zones to populate them.
  let subTab: "construction" | "decor" = "construction";
  let editMode: EditMode = "paint";
  let paintTool: PaintTool = "brush";
  let brush: TerrainId = "grass";
  let cellPx = 16;
  const layerVisible = { sp: true, portal: true, mob: true, npc: true };
  // M14 — décor brush state. brushRadius=0 means 1 tile (point); >0
  // means a square of side (2*r+1) under the cursor. density is the
  // chance to place a décor on each tile inside the brush area on each
  // movement frame (drag-scatter). dragPlacedKeys tracks tiles already
  // touched during the current drag stroke so a tile gets at most one
  // décor per stroke (prevents stacking dozens on the same tile).
  // decorKind starts null to prevent accidental placements before the
  // user explicitly picks a kind from the dropdown.
  let decorKind: string | null = null;
  let decorMode: "place" | "erase" = "place";
  let decorBrushRadius = 0;
  let decorDensity = 1.0;
  const dragPlacedKeys = new Set<string>();
  // M14 v2 — camera-centered iso view state. decorCamera = the tile
  // currently at the canvas center; null = uninitialized, init on next
  // render to the zone's spawn point. decorZoom = iso tile half-height
  // in pixels (so isoTileW = decorZoom * 2). Separate from construction's
  // cellPx so zoom levels don't bleed between the two sub-tabs.
  let decorCamera: { tx: number; ty: number } | null = null;
  let decorZoom = 16;
  const DECOR_ZOOM_MIN = 6;
  const DECOR_ZOOM_MAX = 40;
  const DECOR_ZOOM_STEP = 4;

  // M11 — region viewport. (regionX0, regionY0) is the top-left tile of
  // the editable window; the window has width/height = min(zoneSize,
  // REGION_SIZE). For zones ≤ REGION_SIZE on both axes the viewport
  // covers the whole zone and the mini-map is hidden.
  let regionX0 = 0;
  let regionY0 = 0;

  // Drag state for paint/rect (coords are in GLOBAL tile space, not
  // region-local — every event handler converts mouse → global tile).
  let isPainting = false;
  let dragStart: { x: number; y: number } | null = null;
  let dragCurrent: { x: number; y: number } | null = null;
  // M11 select+paste. `selection` is the persistent outline drawn after
  // a select drag completes (also acts as a reminder of what was copied).
  // `clipboard` is the terrain payload available for paste — survives
  // mode changes and zone switches.
  let selection: { x0: number; y0: number; x1: number; y1: number } | null = null;
  let clipboard: TerrainClipboard | null = null;
  // Preview tile under the cursor in paste mode (top-left of the would-be
  // paste rectangle). Re-rendered on mousemove.
  let pastePreview: { x: number; y: number } | null = null;

  // M11 — undo stack. Capped at 3 entries. Snapshots the mutable bits
  // of the current draft zone (terrain array + 4 entity lists) before
  // any mutation. Zone dimensions / id / displayName / isSafe are NOT
  // tracked — edit them back manually. Cleared on zone switch / new /
  // setZones refresh (intentional: cross-zone undo is confusing).
  type UndoEntry = Pick<
    DraftZone,
    "terrain" | "spawnPoints" | "portals" | "mobSpawns" | "npcSpawns" | "decor"
  >;
  const UNDO_LIMIT = 3;
  let undoStack: UndoEntry[] = [];

  function pushUndo(z: DraftZone): void {
    undoStack.push({
      terrain: z.terrain.slice(),
      spawnPoints: z.spawnPoints.map((s) => ({ ...s })),
      portals: z.portals.map((p) => ({ ...p })),
      mobSpawns: z.mobSpawns.map((m) => ({ ...m })),
      npcSpawns: z.npcSpawns.map((n) => ({ ...n })),
      decor: (z.decor ?? []).map((d) => ({ ...d })),
    });
    while (undoStack.length > UNDO_LIMIT) undoStack.shift();
  }

  function popUndo(): void {
    const z = drafts.find((d) => d.id === selectedId);
    if (!z) return;
    const entry = undoStack.pop();
    if (!entry) return;
    z.terrain = entry.terrain;
    z.spawnPoints = entry.spawnPoints;
    z.portals = entry.portals;
    z.mobSpawns = entry.mobSpawns;
    z.npcSpawns = entry.npcSpawns;
    z.decor = entry.decor;
    markDirty(z);
    renderEditor();
  }

  function regionDims(z: DraftZone): { w: number; h: number } {
    return {
      w: Math.min(z.width, REGION_SIZE),
      h: Math.min(z.height, REGION_SIZE),
    };
  }

  function clampRegionOrigin(z: DraftZone): void {
    const { w, h } = regionDims(z);
    regionX0 = Math.max(0, Math.min(z.width - w, regionX0));
    regionY0 = Math.max(0, Math.min(z.height - h, regionY0));
  }

  function isRegionMode(z: DraftZone): boolean {
    return z.width > REGION_SIZE || z.height > REGION_SIZE;
  }

  // DOM scaffolding
  const listRows = el("div", { class: "items-list-rows" });
  const newBtn = el(
    "button",
    {
      class: "button secondary",
      onclick: () => {
        let n = drafts.length + 1;
        let candidate = `zone_${n}`;
        while (drafts.some((d) => d.id === candidate)) {
          n++;
          candidate = `zone_${n}`;
        }
        const fresh = newBlankZone(candidate);
        drafts.push(fresh);
        selectedId = fresh.id;
        undoStack = [];
        cb.onDirtyChange(true);
        renderList();
        renderEditor();
      },
    },
    "+ New zone",
  );
  const list = el(
    "div",
    { class: "items-list" },
    el("div", { class: "items-list-header" }, newBtn),
    listRows,
  );
  const editorBody = el("div", { class: "editor zone-editor" });
  const root = el("div", { class: "items-layout" }, list, editorBody);

  // Selection + dirty.
  function markDirty(z: DraftZone): void {
    if (!z.__isNew) z.__isDirty = true;
    cb.onDirtyChange(true);
    renderList();
  }

  function renderList(): void {
    listRows.replaceChildren();
    for (const z of drafts.slice().sort((a, b) => a.id.localeCompare(b.id))) {
      listRows.append(
        el(
          "div",
          {
            class:
              "items-list-row" +
              (z.id === selectedId ? " active" : "") +
              (z.__isNew ? " created" : z.__isDirty ? " dirty" : ""),
            onclick: () => {
              selectedId = z.id;
              regionX0 = 0;
              regionY0 = 0;
              selection = null;
              pastePreview = null;
              undoStack = [];
              // M14 v2 — fresh camera on each zone (init lazily to the
              // new zone's spawn point on next decor render).
              decorCamera = null;
              renderList();
              renderEditor();
            },
          },
          el(
            "span",
            { class: "name" },
            z.displayName?.trim() || z.id,
            z.isSafe ? el("span", { class: "zone-safe-badge" }, "safe") : "",
          ),
          el(
            "span",
            { class: "id" },
            z.id,
            " · ",
            String(z.width),
            "×",
            String(z.height),
          ),
        ),
      );
    }
  }

  // Main editor render — header bar, sub-tab nav, then the three-pane
  // body. M14 split: "construction" sub-tab keeps the full top-down
  // editor (terrain paint + entity placement + iso preview); "decor"
  // sub-tab replaces the canvas with an iso view dedicated to décor
  // placement, and trims the side panel to the mini-map only (the
  // small iso preview is redundant when the main canvas IS iso, and
  // entity lists are construction concerns).
  function renderEditor(): void {
    editorBody.replaceChildren();
    const z = drafts.find((d) => d.id === selectedId);
    if (!z) {
      editorBody.append(
        el("div", { class: "empty" }, "Select a zone or create a new one."),
      );
      return;
    }
    clampRegionOrigin(z);
    editorBody.append(
      renderHeader(z),
      renderSubTabNav(),
      subTab === "construction"
        ? el(
            "div",
            { class: "zone-body" },
            el(
              "div",
              { class: "zone-main" },
              renderToolbar(z),
              renderCanvasWrap(z),
            ),
            el(
              "div",
              { class: "zone-side" },
              isRegionMode(z) ? renderMiniMap(z) : el("div", { style: "display:none" }),
              renderIsoPreview(z),
              renderEntityPanel(z),
            ),
          )
        : el(
            "div",
            { class: "zone-body zone-body-decor" },
            // M14 v2 — single column in decor mode. The mini-map side
            // panel is dropped: right-click on canvas + "Center on spawn"
            // are sufficient to navigate, and the canvas now claims the
            // freed width.
            el(
              "div",
              { class: "zone-main zone-main-decor" },
              renderDecorToolbar(z),
              renderDecorIsoCanvas(z),
            ),
          ),
    );
  }

  function renderSubTabNav(): HTMLElement {
    const btn = (key: "construction" | "decor", label: string): HTMLElement =>
      el(
        "button",
        {
          type: "button",
          class: "zone-subtab" + (subTab === key ? " active" : ""),
          onclick: () => {
            if (subTab === key) return;
            subTab = key;
            renderEditor();
          },
        },
        label,
      );
    return el(
      "div",
      { class: "zone-subtab-nav" },
      btn("construction", "Construction"),
      btn("decor", "Décor (iso)"),
    );
  }

  function renderHeader(z: DraftZone): HTMLElement {
    return el(
      "div",
      { class: "editor-section zone-header" },
      el(
        "div",
        { class: "editor-grid" },
        textField("id", z.id, (v) => {
          const old = z.id;
          if (drafts.some((d) => d !== z && d.id === v)) {
            // Reject duplicate; revert input visually on next render.
            renderEditor();
            return;
          }
          z.id = v;
          if (selectedId === old) selectedId = v;
          markDirty(z);
        }),
        textField("Display name", z.displayName ?? "", (v) => {
          z.displayName = v;
          markDirty(z);
          renderList();
        }),
        intField("Width", z.width, ZONE_MIN_SIZE, ZONE_MAX_SIZE, (v) => {
          resizeZone(z, v, z.height);
          markDirty(z);
          renderEditor();
        }),
        intField("Height", z.height, ZONE_MIN_SIZE, ZONE_MAX_SIZE, (v) => {
          resizeZone(z, z.width, v);
          markDirty(z);
          renderEditor();
        }),
        checkboxField("Safe zone (PvP off)", z.isSafe, (v) => {
          z.isSafe = v;
          markDirty(z);
          renderList();
        }),
      ),
      el(
        "div",
        { class: "editor-actions" },
        el(
          "button",
          {
            class: "button danger",
            onclick: () => {
              if (!confirm(`Delete zone "${z.id}"? Any incoming portals will fail server validation on publish.`)) return;
              drafts = drafts.filter((d) => d !== z);
              selectedId = null;
              cb.onDirtyChange(true);
              renderList();
              renderEditor();
            },
          },
          "Delete from draft",
        ),
      ),
    );
  }

  // Resize keeps the existing terrain in the top-left corner; new cells
  // get the default base ("grass"). Trims spawnPoints/portals/mob/npc spawns
  // that fall outside the new bounds (with a confirm if any will be lost).
  function resizeZone(z: DraftZone, newW: number, newH: number): void {
    if (newW === z.width && newH === z.height) return;
    const lostEntities: string[] = [];
    z.spawnPoints = z.spawnPoints.filter((s) => {
      if (s.x < newW && s.y < newH) return true;
      lostEntities.push(`spawnPoint #${s.index}`);
      return false;
    });
    z.portals = z.portals.filter((p) => {
      if (p.x < newW && p.y < newH) return true;
      lostEntities.push(`portal → ${p.targetZoneId}`);
      return false;
    });
    z.mobSpawns = z.mobSpawns.filter((m) => {
      if (m.x < newW && m.y < newH) return true;
      lostEntities.push(`mob ${m.templateId}`);
      return false;
    });
    z.npcSpawns = z.npcSpawns.filter((n) => {
      if (n.x < newW && n.y < newH) return true;
      lostEntities.push(`npc ${n.templateId}`);
      return false;
    });
    // M14 — trim decor placements that fall outside the new bounds.
    if (z.decor) {
      const before = z.decor.length;
      z.decor = z.decor.filter((d) => d.tx < newW && d.ty < newH);
      if (z.decor.length < before) {
        lostEntities.push(`${before - z.decor.length} décor placement(s)`);
      }
    }
    if (lostEntities.length > 0) {
      // Inform admin AFTER the fact — trimming on resize is the expected,
      // documented behavior; a popup would just confuse the flow.
      console.warn(`[zones] resize trimmed: ${lostEntities.join(", ")}`);
    }
    // Reshape terrain — keep top-left, fill new cells with grass.
    const newTerrain: TerrainId[] = new Array(newW * newH).fill("grass");
    const copyW = Math.min(z.width, newW);
    const copyH = Math.min(z.height, newH);
    for (let y = 0; y < copyH; y++) {
      for (let x = 0; x < copyW; x++) {
        newTerrain[ix(newW, x, y)] = z.terrain[ix(z.width, x, y)] ?? "grass";
      }
    }
    z.terrain = newTerrain;
    z.width = newW;
    z.height = newH;
    if (z.spawnPoints.length === 0) {
      // Always need at least one spawn point (Zod min(1)).
      z.spawnPoints.push({ x: Math.floor(newW / 2), y: Math.floor(newH / 2), index: 0 });
    }
  }

  function renderToolbar(z: DraftZone): HTMLElement {
    return el(
      "div",
      { class: "zone-toolbar" },
      // Edit mode chooser.
      el(
        "div",
        { class: "zone-tool-group" },
        el("span", { class: "zone-tool-label" }, "Mode"),
        modeRadio("paint", "Paint terrain"),
        modeRadio("select", "Select"),
        modeRadio("paste", clipboard ? "Paste" : "Paste (no clip)"),
        modeRadio("place_sp", "Place SP"),
        modeRadio("place_portal", "Place portal"),
        modeRadio("place_mob", "Place mob"),
        modeRadio("place_npc", "Place NPC"),
        modeRadio("erase", "Erase entity"),
      ),
      editMode === "paint"
        ? el(
            "div",
            { class: "zone-tool-group" },
            el("span", { class: "zone-tool-label" }, "Tool"),
            paintToolRadio("brush", "Brush"),
            paintToolRadio("rect", "Rectangle"),
            paintToolRadio("bucket", "Bucket"),
            el(
              "button",
              {
                type: "button",
                class: "zone-zoom",
                title: "Fill the currently-visible region with the active terrain",
                onclick: () => fillRegion(z),
              },
              "Fill region",
            ),
            el("span", { class: "zone-tool-label" }, "Terrain"),
            ...TERRAINS.map((t) => paletteSwatch(t)),
          )
        : el("div"),
      el(
        "div",
        { class: "zone-tool-group" },
        el("span", { class: "zone-tool-label" }, "Layers"),
        layerToggle("sp", "SP", "#3ecf6c"),
        layerToggle("portal", "Portal", "#4d8be6"),
        layerToggle("mob", "Mob", "#e64d4d"),
        layerToggle("npc", "NPC", "#e6c73a"),
      ),
      el(
        "div",
        { class: "zone-tool-group" },
        el("span", { class: "zone-tool-label" }, "Zoom"),
        zoomBtn(8),
        zoomBtn(12),
        zoomBtn(16),
        zoomBtn(24),
        el(
          "button",
          {
            type: "button",
            class: "zone-zoom",
            title: "Auto-pick the largest zoom that fits the canvas in the available area",
            onclick: () => fitZoom(z),
          },
          "Fit",
        ),
      ),
      el(
        "div",
        { class: "zone-tool-group" },
        el(
          "button",
          {
            type: "button",
            class: "zone-zoom",
            title: `Undo (Ctrl+Z) — up to ${UNDO_LIMIT} steps. Resets when you switch zones.`,
            disabled: undoStack.length === 0,
            onclick: () => popUndo(),
          },
          `↶ Undo (${undoStack.length}/${UNDO_LIMIT})`,
        ),
      ),
    );
  }

  function modeRadio(value: EditMode, label: string): HTMLElement {
    return el(
      "label",
      { class: "zone-radio" + (editMode === value ? " active" : "") },
      el("input", {
        type: "radio",
        name: "zone-mode",
        checked: editMode === value,
        onchange: () => {
          editMode = value;
          renderEditor();
        },
      }),
      label,
    );
  }

  function paintToolRadio(value: PaintTool, label: string): HTMLElement {
    return el(
      "label",
      { class: "zone-radio" + (paintTool === value ? " active" : "") },
      el("input", {
        type: "radio",
        name: "zone-paint-tool",
        checked: paintTool === value,
        onchange: () => {
          paintTool = value;
          renderEditor();
        },
      }),
      label,
    );
  }

  function paletteSwatch(t: TerrainId): HTMLElement {
    return el(
      "button",
      {
        type: "button",
        class: "zone-swatch" + (brush === t ? " active" : ""),
        title: TERRAIN_LABEL[t] + (NON_BLOCKING.has(t) ? " (walkable)" : " (blocks)"),
        style: `background:${TERRAIN_COLOR[t]}`,
        onclick: () => {
          brush = t;
          renderEditor();
        },
      },
      TERRAIN_LABEL[t][0]!,
    );
  }

  // M14 v2 — décor mode toolbar.
  // - Kind dropdown: defaults to "— Select a kind —" placeholder (no
  //   auto-selection) so clicks before picking do nothing instead of
  //   placing an accidental oak.
  // - Mode radio: "Place" / "Erase" — controls what left-click does on
  //   the iso canvas (replaces the old right-click-to-erase since
  //   right-click is now reserved for camera centering).
  // - Zoom −/+ buttons: separate from construction's cellPx, clamps to
  //   [DECOR_ZOOM_MIN, DECOR_ZOOM_MAX].
  // - Brush radius / density: unchanged.
  // - "Center on spawn" button: resets camera to the zone's spawn point
  //   for the user to find their bearings.
  // - "@(tx,ty)" coords label: shows current camera target.
  function renderDecorToolbar(z: DraftZone): HTMLElement {
    const select = document.createElement("select");
    select.className = "zone-decor-select";
    // Placeholder option — when selected, decorKind = null.
    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = "— Select a kind —";
    if (decorKind === null) placeholder.selected = true;
    select.appendChild(placeholder);
    // Group remaining options by category for readability.
    const byCat: Record<string, string[]> = {};
    for (const k of DECOR_KINDS) {
      const cat = decorCategoryOf(k) ?? "other";
      (byCat[cat] ??= []).push(k);
    }
    for (const [cat, kinds] of Object.entries(byCat)) {
      const og = document.createElement("optgroup");
      og.label = cat;
      for (const k of kinds) {
        const opt = document.createElement("option");
        opt.value = k;
        opt.textContent = k;
        if (k === decorKind) opt.selected = true;
        og.appendChild(opt);
      }
      select.appendChild(og);
    }
    select.addEventListener("change", () => {
      decorKind = select.value === "" ? null : select.value;
    });

    const modeRadio2 = (m: "place" | "erase", label: string): HTMLElement =>
      el(
        "label",
        { class: "zone-radio" + (decorMode === m ? " active" : "") },
        el("input", {
          type: "radio",
          name: "decor-mode",
          checked: decorMode === m,
          onchange: () => {
            decorMode = m;
            renderEditor();
          },
        }),
        label,
      );

    const radiusRadio = (r: number, label: string): HTMLElement =>
      el(
        "label",
        { class: "zone-radio" + (decorBrushRadius === r ? " active" : "") },
        el("input", {
          type: "radio",
          name: "decor-radius",
          checked: decorBrushRadius === r,
          onchange: () => {
            decorBrushRadius = r;
            renderEditor();
          },
        }),
        label,
      );

    const densityInput = document.createElement("input");
    densityInput.type = "range";
    densityInput.min = "0";
    densityInput.max = "100";
    densityInput.step = "5";
    densityInput.value = String(Math.round(decorDensity * 100));
    densityInput.style.width = "120px";
    const densityLabel = el(
      "span",
      { class: "zone-tool-label" },
      `Density: ${Math.round(decorDensity * 100)}%`,
    );
    densityInput.addEventListener("input", () => {
      decorDensity = Number(densityInput.value) / 100;
      densityLabel.textContent = `Density: ${Math.round(decorDensity * 100)}%`;
    });

    const zoomBtnLocal = (delta: number, label: string, title: string): HTMLElement =>
      el(
        "button",
        {
          type: "button",
          class: "zone-zoom",
          title,
          onclick: () => {
            const next = Math.max(
              DECOR_ZOOM_MIN,
              Math.min(DECOR_ZOOM_MAX, decorZoom + delta),
            );
            if (next === decorZoom) return;
            decorZoom = next;
            renderEditor();
          },
        },
        label,
      );

    const count = (z.decor ?? []).length;
    const cam = decorCamera ?? z.spawnPoints[0] ?? { tx: 0, ty: 0 };
    const camTx = "tx" in cam ? cam.tx : (cam as { x: number }).x;
    const camTy = "ty" in cam ? cam.ty : (cam as { y: number }).y;

    return el(
      "div",
      { class: "zone-tool-group" },
      el("span", { class: "zone-tool-label" }, "Kind"),
      select,
      el("span", { class: "zone-tool-label" }, "Mode"),
      modeRadio2("place", "Place"),
      modeRadio2("erase", "Erase"),
      el("span", { class: "zone-tool-label" }, "Brush"),
      radiusRadio(0, "1×1"),
      radiusRadio(1, "3×3"),
      radiusRadio(2, "5×5"),
      densityLabel,
      densityInput,
      el("span", { class: "zone-tool-label" }, "Zoom"),
      zoomBtnLocal(-DECOR_ZOOM_STEP, "−", "Zoom out (see more)"),
      el("span", { class: "zone-tool-label" }, `${decorZoom}px`),
      zoomBtnLocal(DECOR_ZOOM_STEP, "+", "Zoom in (more precision)"),
      el(
        "button",
        {
          type: "button",
          class: "zone-zoom",
          title: "Center camera on the zone's primary spawn point",
          onclick: () => {
            const sp = z.spawnPoints[0];
            decorCamera = sp ? { tx: sp.x, ty: sp.y } : { tx: 0, ty: 0 };
            renderEditor();
          },
        },
        "Center on spawn",
      ),
      el(
        "span",
        { class: "zone-tool-label", style: "margin-left:12px" },
        `@(${camTx},${camTy})  ·  Total: ${count}`,
      ),
      el(
        "button",
        {
          type: "button",
          class: "zone-zoom",
          title: "Remove every décor placement from this zone (undoable)",
          onclick: () => {
            if (!confirm(`Remove ALL ${count} décor placements from this zone?`)) return;
            pushUndo(z);
            z.decor = [];
            markDirty(z);
            renderEditor();
          },
        },
        "Clear all",
      ),
    );
  }

  function layerToggle(
    key: keyof typeof layerVisible,
    label: string,
    color: string,
  ): HTMLElement {
    return el(
      "label",
      { class: "zone-radio" + (layerVisible[key] ? " active" : "") },
      el("input", {
        type: "checkbox",
        checked: layerVisible[key],
        onchange: (e: Event) => {
          layerVisible[key] = (e.target as HTMLInputElement).checked;
          renderEditor();
        },
      }),
      el("span", { class: "zone-layer-dot", style: `background:${color}` }),
      label,
    );
  }

  function zoomBtn(px: number): HTMLElement {
    return el(
      "button",
      {
        type: "button",
        class: "zone-zoom" + (cellPx === px ? " active" : ""),
        onclick: () => {
          cellPx = px;
          renderEditor();
        },
      },
      `${px}px`,
    );
  }

  // 4-connectivity flood fill (paint bucket). Replaces the connected
  // region of "same terrain as the clicked tile" with the brush color.
  // Stops at terrain boundaries — perfect for filling a building
  // interior after you've drawn its stone walls. Iterative DFS via a
  // stack — recursive blows the JS stack on uniform 256+ zones.
  function bucketFill(z: DraftZone, sx: number, sy: number, replacement: TerrainId): void {
    const target = z.terrain[ix(z.width, sx, sy)] ?? "grass";
    if (target === replacement) return;
    const stack: number[] = [sx, sy];
    while (stack.length > 0) {
      const y = stack.pop()!;
      const x = stack.pop()!;
      if (x < 0 || y < 0 || x >= z.width || y >= z.height) continue;
      const i = ix(z.width, x, y);
      if (z.terrain[i] !== target) continue;
      z.terrain[i] = replacement;
      stack.push(x + 1, y, x - 1, y, x, y + 1, x, y - 1);
    }
    markDirty(z);
  }

  // Fill the currently-visible region (the window framed by the yellow
  // mini-map rect) with the active brush terrain. Only touches tiles
  // inside [regionX0..regionX0+rw, regionY0..regionY0+rh) — pan the
  // mini-map and re-click to fill another patch. Far less destructive
  // than a full-zone fill on a 1024² zone where one slip wipes 1 M tiles.
  function fillRegion(z: DraftZone): void {
    pushUndo(z);
    const { w: rw, h: rh } = regionDims(z);
    const xMax = Math.min(regionX0 + rw, z.width);
    const yMax = Math.min(regionY0 + rh, z.height);
    for (let y = regionY0; y < yMax; y++) {
      for (let x = regionX0; x < xMax; x++) {
        z.terrain[ix(z.width, x, y)] = brush;
      }
    }
    markDirty(z);
    renderEditor();
  }

  // Auto-pick the largest cellPx that fits the entire region in the
  // canvas wrap's available area (both axes). Reads the wrap's actual
  // clientWidth/Height *after* the layout is settled — we measure on
  // click rather than predicting, so window resizes are picked up too.
  function fitZoom(z: DraftZone): void {
    const wrap = editorBody.querySelector(".zone-canvas-wrap") as HTMLElement | null;
    if (!wrap) return;
    const availW = wrap.clientWidth - 16; // padding + border slack
    const availH = wrap.clientHeight - 16;
    const { w, h } = regionDims(z);
    const px = Math.floor(Math.min(availW / w, availH / h));
    cellPx = Math.max(2, Math.min(48, px));
    renderEditor();
  }

  // ---- Top-down canvas ---------------------------------------------------

  function renderCanvasWrap(z: DraftZone): HTMLElement {
    const { w: rw, h: rh } = regionDims(z);
    const canvas = document.createElement("canvas");
    canvas.className = "zone-canvas";
    canvas.width = rw * cellPx;
    canvas.height = rh * cellPx;
    canvas.style.width = `${canvas.width}px`;
    canvas.style.height = `${canvas.height}px`;
    const ctx = canvas.getContext("2d")!;

    const draw = (): void => drawTopDown(ctx, z);
    draw();

    // ---- Mouse handling. Coords map to GLOBAL tile via region offset. --
    function tileAt(ev: MouseEvent): { x: number; y: number } | null {
      const rect = canvas.getBoundingClientRect();
      const localX = Math.floor((ev.clientX - rect.left) / cellPx);
      const localY = Math.floor((ev.clientY - rect.top) / cellPx);
      const tx = regionX0 + localX;
      const ty = regionY0 + localY;
      if (tx < 0 || ty < 0 || tx >= z.width || ty >= z.height) return null;
      return { x: tx, y: ty };
    }

    canvas.addEventListener("mousedown", (ev) => {
      const t = tileAt(ev);
      if (!t) return;
      if (editMode === "paint") {
        if (paintTool === "brush") {
          // Snapshot at stroke start so the whole drag is one undo entry.
          pushUndo(z);
          isPainting = true;
          z.terrain[ix(z.width, t.x, t.y)] = brush;
          markDirty(z);
          drawTopDown(ctx, z);
          renderIsoCanvas(z);
        } else if (paintTool === "rect") {
          // Snapshot taken at endDrag instead — we don't want to push
          // until the user actually commits the rectangle.
          dragStart = t;
          dragCurrent = t;
          drawTopDown(ctx, z);
        } else if (paintTool === "bucket") {
          pushUndo(z);
          bucketFill(z, t.x, t.y, brush);
          drawTopDown(ctx, z);
          renderIsoCanvas(z);
          if (isRegionMode(z)) drawMiniMap(z);
        }
        return;
      }
      if (editMode === "select") {
        dragStart = t;
        dragCurrent = t;
        drawTopDown(ctx, z);
        return;
      }
      if (editMode === "paste") {
        if (!clipboard) {
          alert("Nothing copied yet. Switch to Select mode, drag a rectangle, then come back to Paste.");
          return;
        }
        pushUndo(z);
        pasteClipboardAt(z, t.x, t.y);
        drawTopDown(ctx, z);
        renderIsoCanvas(z);
        if (isRegionMode(z)) drawMiniMap(z);
        return;
      }
      // Place / erase modes.
      pushUndo(z);
      handleEntityClick(z, t);
      drawTopDown(ctx, z);
      renderIsoCanvas(z);
      // Side panel needs a refresh after entity changes.
      const newSide = renderEntityPanel(z);
      const oldSide = editorBody.querySelector(".zone-entity-panel");
      if (oldSide && oldSide.parentElement) {
        oldSide.parentElement.replaceChild(newSide, oldSide);
      }
    });

    canvas.addEventListener("mousemove", (ev) => {
      const t = tileAt(ev);
      if (!t) return;
      if (editMode === "paint" && paintTool === "brush" && isPainting) {
        z.terrain[ix(z.width, t.x, t.y)] = brush;
        markDirty(z);
        drawTopDown(ctx, z);
        renderIsoCanvas(z);
      } else if (editMode === "paint" && paintTool === "rect" && dragStart) {
        dragCurrent = t;
        drawTopDown(ctx, z);
      } else if (editMode === "select" && dragStart) {
        dragCurrent = t;
        drawTopDown(ctx, z);
      } else if (editMode === "paste" && clipboard) {
        // Live ghost rectangle following the cursor — clamps so the
        // top-left lands inside the zone (the right/bottom edge may
        // still spill; pasteClipboardAt clips at paste time).
        pastePreview = { x: t.x, y: t.y };
        drawTopDown(ctx, z);
      }
    });

    function endDrag(ev: MouseEvent): void {
      if (editMode === "paint" && paintTool === "brush" && isPainting) {
        isPainting = false;
        return;
      }
      if (editMode === "paint" && paintTool === "rect" && dragStart) {
        const t = tileAt(ev) ?? dragCurrent ?? dragStart;
        const x0 = Math.min(dragStart.x, t.x);
        const y0 = Math.min(dragStart.y, t.y);
        const x1 = Math.max(dragStart.x, t.x);
        const y1 = Math.max(dragStart.y, t.y);
        pushUndo(z);
        for (let y = y0; y <= y1; y++) {
          for (let x = x0; x <= x1; x++) {
            z.terrain[ix(z.width, x, y)] = brush;
          }
        }
        dragStart = null;
        dragCurrent = null;
        markDirty(z);
        drawTopDown(ctx, z);
        renderIsoCanvas(z);
        return;
      }
      if (editMode === "select" && dragStart) {
        const t = tileAt(ev) ?? dragCurrent ?? dragStart;
        const x0 = Math.min(dragStart.x, t.x);
        const y0 = Math.min(dragStart.y, t.y);
        const x1 = Math.max(dragStart.x, t.x);
        const y1 = Math.max(dragStart.y, t.y);
        selection = { x0, y0, x1, y1 };
        // Snapshot the terrain in the selection rectangle into the
        // clipboard. Width = (x1 - x0 + 1) since both ends are inclusive.
        const w = x1 - x0 + 1;
        const h = y1 - y0 + 1;
        const buf: TerrainId[] = new Array(w * h);
        for (let dy = 0; dy < h; dy++) {
          for (let dx = 0; dx < w; dx++) {
            buf[dy * w + dx] = z.terrain[ix(z.width, x0 + dx, y0 + dy)] ?? "grass";
          }
        }
        clipboard = { width: w, height: h, terrain: buf };
        dragStart = null;
        dragCurrent = null;
        drawTopDown(ctx, z);
        // Refresh the toolbar so the "Paste" label updates from
        // "(no clip)" → "Paste".
        renderEditor();
      }
    }
    canvas.addEventListener("mouseup", endDrag);
    canvas.addEventListener("mouseleave", (ev) => {
      // End brush stroke on leaving canvas; rect stays armed (mouseup
      // outside still applies the rect via the document-level fallback).
      isPainting = false;
      if (editMode === "paint" && paintTool === "rect" && dragStart) {
        endDrag(ev);
      }
      if (editMode === "select" && dragStart) {
        endDrag(ev);
      }
      // Clear the paste ghost so it doesn't linger on the side panel
      // when the cursor leaves the canvas.
      if (editMode === "paste" && pastePreview) {
        pastePreview = null;
        drawTopDown(ctx, z);
      }
    });

    return el("div", { class: "zone-canvas-wrap" }, canvas);
  }

  // M14 — iso canvas for décor placement. Mirrors in-game projection.
  // Uses the same region viewport as the top-down canvas (regionX0/Y0
  // + REGION_SIZE), but renders diamond tiles and decor sprites
  // (lazy-loaded via Image()).
  //
  // Sprite image cache — Image() loads keyed by asset path. Returns
  // null while loading or on failure (the renderer falls back to a
  // colored category marker). onAnyLoaded triggers a redraw so freshly
  // arrived sprites appear without user interaction.
  const decorSpriteCache = new Map<string, HTMLImageElement | null>();
  const decorSpriteLoading = new Set<string>();

  function getDecorSprite(
    path: string,
    onLoaded: () => void,
  ): HTMLImageElement | null {
    const cached = decorSpriteCache.get(path);
    if (cached !== undefined) return cached;
    if (decorSpriteLoading.has(path)) return null;
    decorSpriteLoading.add(path);
    const img = new Image();
    img.onload = () => {
      decorSpriteCache.set(path, img);
      decorSpriteLoading.delete(path);
      onLoaded();
    };
    img.onerror = () => {
      decorSpriteCache.set(path, null);
      decorSpriteLoading.delete(path);
    };
    img.src = path;
    return null;
  }

  // M14 v2 — camera-centered iso canvas. Mirrors what the player sees:
  // fixed viewport size, only renders tiles around the camera position
  // instead of dumping the whole 64×64 region into a giant iso diamond
  // with massive black borders.
  //
  // Navigation:
  //   - Right-click on any tile → centers the camera on it (killer feature)
  //   - Mini-map click → also centers the camera (see decor mode branch
  //     in renderMiniMap's panTo)
  //   - "Center on spawn" button in the toolbar
  // Placement (left button):
  //   - decorMode === "place" + a kind is selected → places décor
  //   - decorMode === "erase" → removes décor in the brush area
  //   - drag while in place mode → scatter with density (per-tile chance)
  // Zoom: decorZoom slider in toolbar (separate from construction cellPx).
  // M14 v2 — responsive canvas sizing. The decor iso canvas claims the
  // editor's full width (mini-map is gone). We compute target dims from
  // window.innerWidth/innerHeight at render time and apply sensible
  // caps so a 4K monitor doesn't end up with a 3500px-wide canvas (more
  // pixels than useful, and PixiJS-style render cost grows with area).
  // Doesn't react to window resize live — would need a ResizeObserver
  // and a draw redispatch; acceptable for now since the user normally
  // re-renders on every interaction anyway.
  const DECOR_CANVAS_MAX_W = 2200;
  const DECOR_CANVAS_MAX_H = 1100;
  // Approximate space taken by the admin sidebar (~250px) + zone list
  // (~280px) + editor padding. Empirical on 1920×1080 baseline.
  const DECOR_CANVAS_RESERVED_W = 580;
  const DECOR_CANVAS_RESERVED_H = 320;

  function decorCanvasDims(): { w: number; h: number } {
    const w = Math.max(
      720,
      Math.min(DECOR_CANVAS_MAX_W, window.innerWidth - DECOR_CANVAS_RESERVED_W),
    );
    const h = Math.max(
      480,
      Math.min(DECOR_CANVAS_MAX_H, window.innerHeight - DECOR_CANVAS_RESERVED_H),
    );
    return { w, h };
  }

  function renderDecorIsoCanvas(z: DraftZone): HTMLElement {
    // Lazy-init the camera to the zone's primary spawn point on the
    // first render after a zone switch (or first ever entry).
    if (decorCamera === null) {
      const sp = z.spawnPoints[0];
      decorCamera = sp
        ? { tx: sp.x, ty: sp.y }
        : { tx: Math.floor(z.width / 2), ty: Math.floor(z.height / 2) };
    }
    const cam = decorCamera;

    const isoTileW = decorZoom * 2;
    const isoTileH = decorZoom;
    const { w: canvasW, h: canvasH } = decorCanvasDims();
    const canvas = document.createElement("canvas");
    canvas.className = "zone-canvas zone-canvas-iso";
    canvas.width = canvasW;
    canvas.height = canvasH;
    canvas.style.width = `${canvas.width}px`;
    canvas.style.height = `${canvas.height}px`;
    // Hijack the browser context menu so right-click can mean "center camera".
    canvas.addEventListener("contextmenu", (ev) => ev.preventDefault());
    const ctx = canvas.getContext("2d")!;

    // Camera origin: the screen position where the CENTER of (cam.tx, cam.ty)
    // lands. Always the middle of the canvas — that's the whole point of
    // camera-centered rendering.
    const camScreenX = canvas.width / 2;
    const camScreenY = canvas.height / 2;

    function tileToIso(tx: number, ty: number): { sx: number; sy: number } {
      // Standard 2:1 dimetric: a tile's center is at
      //   (camScreenX + (tx-cam.tx - (ty-cam.ty)) * isoTileW/2,
      //    camScreenY + (tx-cam.tx + (ty-cam.ty)) * isoTileH/2)
      // The diamond's TOP vertex (used as draw origin for fillPath) is
      // half a tile-height above its center.
      const dx = tx - cam.tx;
      const dy = ty - cam.ty;
      const sx = camScreenX + (dx - dy) * (isoTileW / 2);
      const sy = camScreenY + (dx + dy) * (isoTileH / 2) - isoTileH / 2;
      return { sx, sy };
    }

    function isoToTile(px: number, py: number): { tx: number; ty: number } | null {
      // Inverse of tileToIso. Map pixel relative to camera, then unproject.
      const dpx = px - camScreenX;
      const dpy = py - camScreenY;
      const a = dpx / (isoTileW / 2);
      const b = dpy / (isoTileH / 2);
      const localTx = Math.round((a + b) / 2);
      const localTy = Math.round((b - a) / 2);
      const tx = cam.tx + localTx;
      const ty = cam.ty + localTy;
      if (tx < 0 || ty < 0 || tx >= z.width || ty >= z.height) return null;
      return { tx, ty };
    }

    // Compute the visible tile range. Each tile's center is at most
    // (canvas.width/2 + isoTileW) horizontal distance from the camera
    // when it's still partially visible. A tile center N steps east of
    // the camera maps to N * isoTileW/2 horizontal + N * isoTileH/2
    // vertical, so we need both ranges. Conservative: use the larger
    // of horizontal/vertical bound and expand by 1 for the iso half-tile
    // overlap at the edges.
    function visibleTileBounds(): { x0: number; y0: number; x1: number; y1: number } {
      const halfW = canvas.width / 2;
      const halfH = canvas.height / 2;
      const maxDxEast = Math.ceil(halfW / (isoTileW / 2)) + 2;
      const maxDyNorth = Math.ceil(halfH / (isoTileH / 2)) + 2;
      // The diamond's diagonals on the ground plane: tiles to the east
      // (+x) appear lower-right, north (-y) appears upper-right. So
      // visible tile bounding box uses MAX of both diagonals.
      const radius = maxDxEast + maxDyNorth;
      return {
        x0: Math.max(0, cam.tx - radius),
        y0: Math.max(0, cam.ty - radius),
        x1: Math.min(z.width, cam.tx + radius + 1),
        y1: Math.min(z.height, cam.ty + radius + 1),
      };
    }

    let needsRedraw = false;
    const requestRedraw = (): void => {
      if (needsRedraw) return;
      needsRedraw = true;
      requestAnimationFrame(() => {
        needsRedraw = false;
        draw();
      });
    };

    function draw(): void {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const { x0, y0, x1, y1 } = visibleTileBounds();

      // Terrain diamonds — only the tiles likely visible.
      for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
          const t = z.terrain[ix(z.width, x, y)] ?? "grass";
          const { sx, sy } = tileToIso(x, y);
          // Quick cull: skip tiles whose diamond is fully off-canvas.
          if (sx + isoTileW < 0 || sx - isoTileW > canvas.width) continue;
          if (sy + isoTileH * 2 < 0 || sy - isoTileH > canvas.height) continue;
          ctx.fillStyle = TERRAIN_COLOR[t];
          ctx.beginPath();
          ctx.moveTo(sx, sy);
          ctx.lineTo(sx + isoTileW / 2, sy + isoTileH / 2);
          ctx.lineTo(sx, sy + isoTileH);
          ctx.lineTo(sx - isoTileW / 2, sy + isoTileH / 2);
          ctx.closePath();
          ctx.fill();
        }
      }

      // Décor — sort by ty so back rows render first (overlap correct).
      const visibleDecor = (z.decor ?? []).filter(
        (d) => d.tx >= x0 && d.ty >= y0 && d.tx < x1 && d.ty < y1,
      );
      visibleDecor.sort((a, b) => a.ty - b.ty || a.tx - b.tx);
      // Match game scale: in-game uses TILE_W=64 with WORLD_SCALE=1.6 →
      // effective 102.4 px/tile. Admin iso shows tiles at isoTileW
      // pixels wide; sprite scale = isoTileW / 64 keeps the sprite/tile
      // proportions identical to in-game.
      const spriteScale = isoTileW / 64;
      for (const d of visibleDecor) {
        const path = DECOR_SPRITES[d.kind];
        const { sx, sy } = tileToIso(d.tx, d.ty);
        const baseY = sy + isoTileH / 2;
        const img = path ? getDecorSprite(path, requestRedraw) : null;
        if (img) {
          const w = img.naturalWidth * spriteScale;
          const h = img.naturalHeight * spriteScale;
          ctx.drawImage(img, sx - w / 2, baseY - h, w, h);
        } else {
          const cat = decorCategoryOf(d.kind) ?? "trees";
          ctx.fillStyle = DECOR_CATEGORY_COLOR[cat];
          ctx.strokeStyle = "rgba(0,0,0,0.4)";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(sx, baseY - isoTileH * 0.6, Math.max(3, isoTileH * 0.5), 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
      }

      // Brush cursor overlay — color depends on mode (gold for place,
      // red-ish for erase) and presence of a kind (dim if none picked).
      if (hoverTile) {
        const r = decorBrushRadius;
        const stroke =
          decorMode === "erase"
            ? "rgba(255,80,80,0.85)"
            : decorKind === null
            ? "rgba(180,180,180,0.55)"
            : "rgba(255,220,80,0.85)";
        ctx.strokeStyle = stroke;
        ctx.lineWidth = 1.5;
        for (let dy = -r; dy <= r; dy++) {
          for (let dx = -r; dx <= r; dx++) {
            const tx = hoverTile.tx + dx;
            const ty = hoverTile.ty + dy;
            if (tx < 0 || ty < 0 || tx >= z.width || ty >= z.height) continue;
            const { sx, sy } = tileToIso(tx, ty);
            ctx.beginPath();
            ctx.moveTo(sx, sy);
            ctx.lineTo(sx + isoTileW / 2, sy + isoTileH / 2);
            ctx.lineTo(sx, sy + isoTileH);
            ctx.lineTo(sx - isoTileW / 2, sy + isoTileH / 2);
            ctx.closePath();
            ctx.stroke();
          }
        }
      }
    }

    let hoverTile: { tx: number; ty: number } | null = null;
    let isDragging = false;
    let isErasing = false;

    function tileFromEvent(ev: MouseEvent): { tx: number; ty: number } | null {
      const rect = canvas.getBoundingClientRect();
      const px = ev.clientX - rect.left;
      const py = ev.clientY - rect.top;
      return isoToTile(px, py);
    }

    function placeAt(tile: { tx: number; ty: number }, allowDensity: boolean): boolean {
      if (decorKind === null) return false;
      const kind = decorKind;
      let any = false;
      const r = decorBrushRadius;
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          const tx = tile.tx + dx;
          const ty = tile.ty + dy;
          if (tx < 0 || ty < 0 || tx >= z.width || ty >= z.height) continue;
          const key = `${tx},${ty}`;
          if (dragPlacedKeys.has(key)) continue;
          if (allowDensity && Math.random() >= decorDensity) {
            dragPlacedKeys.add(key);
            continue;
          }
          dragPlacedKeys.add(key);
          z.decor ??= [];
          z.decor.push({ kind, tx, ty });
          any = true;
        }
      }
      return any;
    }

    function eraseAt(tile: { tx: number; ty: number }): boolean {
      const r = decorBrushRadius;
      const x0 = tile.tx - r;
      const x1 = tile.tx + r;
      const y0 = tile.ty - r;
      const y1 = tile.ty + r;
      const before = (z.decor ?? []).length;
      z.decor = (z.decor ?? []).filter(
        (d) => d.tx < x0 || d.tx > x1 || d.ty < y0 || d.ty > y1,
      );
      return z.decor.length !== before;
    }

    canvas.addEventListener("mousedown", (ev) => {
      const tile = tileFromEvent(ev);
      if (!tile) return;
      // Right-click → center camera. No undo (purely cosmetic nav).
      if (ev.button === 2) {
        decorCamera = { tx: tile.tx, ty: tile.ty };
        renderEditor();
        return;
      }
      pushUndo(z);
      dragPlacedKeys.clear();
      if (decorMode === "erase") {
        isErasing = true;
        if (eraseAt(tile)) {
          markDirty(z);
          renderEditor();
        }
        return;
      }
      // Place mode. Requires a kind picked; otherwise click is no-op.
      if (decorKind === null) return;
      isDragging = true;
      if (placeAt(tile, false)) {
        markDirty(z);
        draw();
        // Light update of the iso preview side panel too (terrain unchanged
        // but the preview also shows decor markers). Cheap.
        renderIsoCanvas(z);
      }
    });

    canvas.addEventListener("mousemove", (ev) => {
      const tile = tileFromEvent(ev);
      if (!tile) {
        if (hoverTile) {
          hoverTile = null;
          draw();
        }
        return;
      }
      if (!hoverTile || hoverTile.tx !== tile.tx || hoverTile.ty !== tile.ty) {
        hoverTile = tile;
        if (isDragging) {
          if (placeAt(tile, true)) {
            markDirty(z);
            renderIsoCanvas(z);
          }
        } else if (isErasing) {
          if (eraseAt(tile)) {
            markDirty(z);
            renderIsoCanvas(z);
          }
        }
        draw();
      }
    });

    const endStroke = (): void => {
      if (isDragging || isErasing) {
        isDragging = false;
        isErasing = false;
        dragPlacedKeys.clear();
        // Re-render the editor so the toolbar's "Total: N" updates.
        renderEditor();
      }
    };
    canvas.addEventListener("mouseup", endStroke);
    canvas.addEventListener("mouseleave", () => {
      hoverTile = null;
      draw();
      endStroke();
    });

    draw();
    return el(
      "div",
      { class: "zone-canvas-wrap zone-canvas-wrap-iso" },
      canvas,
    );
  }

  function drawTopDown(ctx: CanvasRenderingContext2D, z: DraftZone): void {
    const { w: rw, h: rh } = regionDims(z);
    const xMax = Math.min(regionX0 + rw, z.width);
    const yMax = Math.min(regionY0 + rh, z.height);
    // Terrain — only the visible region.
    for (let y = regionY0; y < yMax; y++) {
      for (let x = regionX0; x < xMax; x++) {
        const t = z.terrain[ix(z.width, x, y)] ?? "grass";
        ctx.fillStyle = TERRAIN_COLOR[t];
        ctx.fillRect((x - regionX0) * cellPx, (y - regionY0) * cellPx, cellPx, cellPx);
      }
    }
    // Light grid lines (subtle, only when zoomed in enough).
    if (cellPx >= 12) {
      ctx.strokeStyle = "rgba(0,0,0,0.18)";
      ctx.lineWidth = 1;
      for (let x = 0; x <= rw; x++) {
        ctx.beginPath();
        ctx.moveTo(x * cellPx + 0.5, 0);
        ctx.lineTo(x * cellPx + 0.5, rh * cellPx);
        ctx.stroke();
      }
      for (let y = 0; y <= rh; y++) {
        ctx.beginPath();
        ctx.moveTo(0, y * cellPx + 0.5);
        ctx.lineTo(rw * cellPx, y * cellPx + 0.5);
        ctx.stroke();
      }
    }
    // Entity overlays — clip to region.
    function inRegion(tx: number, ty: number): boolean {
      return tx >= regionX0 && ty >= regionY0 && tx < xMax && ty < yMax;
    }
    if (layerVisible.sp) {
      for (const s of z.spawnPoints) if (inRegion(s.x, s.y)) {
        drawDot(ctx, s.x, s.y, "#3ecf6c", `SP${s.index}`);
      }
    }
    if (layerVisible.portal) {
      for (const p of z.portals) if (inRegion(p.x, p.y)) {
        drawDot(ctx, p.x, p.y, "#4d8be6", `→${p.targetZoneId.slice(0, 6)}#${p.targetSpawnIndex}`);
      }
    }
    if (layerVisible.mob) {
      for (const m of z.mobSpawns) if (inRegion(m.x, m.y)) {
        drawDot(ctx, m.x, m.y, "#e64d4d", m.templateId.slice(0, 6));
      }
    }
    if (layerVisible.npc) {
      for (const n of z.npcSpawns) if (inRegion(n.x, n.y)) {
        drawDot(ctx, n.x, n.y, "#e6c73a", n.templateId.slice(0, 6));
      }
    }
    // Select-mode drag preview (live yellow dashed rect, no fill).
    if (editMode === "select" && dragStart && dragCurrent) {
      const sx0 = Math.max(regionX0, Math.min(dragStart.x, dragCurrent.x));
      const sy0 = Math.max(regionY0, Math.min(dragStart.y, dragCurrent.y));
      const sx1 = Math.min(xMax - 1, Math.max(dragStart.x, dragCurrent.x));
      const sy1 = Math.min(yMax - 1, Math.max(dragStart.y, dragCurrent.y));
      if (sx1 >= sx0 && sy1 >= sy0) {
        ctx.save();
        ctx.setLineDash([6, 4]);
        ctx.strokeStyle = "#f5cc66";
        ctx.lineWidth = 2;
        ctx.strokeRect(
          (sx0 - regionX0) * cellPx + 1,
          (sy0 - regionY0) * cellPx + 1,
          (sx1 - sx0 + 1) * cellPx - 2,
          (sy1 - sy0 + 1) * cellPx - 2,
        );
        ctx.restore();
      }
    }
    // Persistent selection outline (visible across modes after a copy).
    if (selection && (editMode === "select" || editMode === "paste")) {
      const sx0 = Math.max(regionX0, selection.x0);
      const sy0 = Math.max(regionY0, selection.y0);
      const sx1 = Math.min(xMax - 1, selection.x1);
      const sy1 = Math.min(yMax - 1, selection.y1);
      if (sx1 >= sx0 && sy1 >= sy0) {
        ctx.save();
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = "#3ecf6c";
        ctx.lineWidth = 1.5;
        ctx.strokeRect(
          (sx0 - regionX0) * cellPx + 0.5,
          (sy0 - regionY0) * cellPx + 0.5,
          (sx1 - sx0 + 1) * cellPx - 1,
          (sy1 - sy0 + 1) * cellPx - 1,
        );
        ctx.restore();
      }
    }
    // Paste-mode hover preview — translucent terrain tiles under cursor.
    if (editMode === "paste" && pastePreview && clipboard) {
      const px = pastePreview.x;
      const py = pastePreview.y;
      const wMax = Math.min(clipboard.width, z.width - px);
      const hMax = Math.min(clipboard.height, z.height - py);
      for (let dy = 0; dy < hMax; dy++) {
        for (let dx = 0; dx < wMax; dx++) {
          const tx = px + dx;
          const ty = py + dy;
          if (tx < regionX0 || ty < regionY0 || tx >= xMax || ty >= yMax) continue;
          const t = clipboard.terrain[dy * clipboard.width + dx] ?? "grass";
          ctx.fillStyle = TERRAIN_COLOR[t] + "b0"; // 70% alpha
          ctx.fillRect(
            (tx - regionX0) * cellPx,
            (ty - regionY0) * cellPx,
            cellPx,
            cellPx,
          );
        }
      }
      // Outline the would-be paste footprint.
      ctx.save();
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      ctx.strokeRect(
        (px - regionX0) * cellPx,
        (py - regionY0) * cellPx,
        wMax * cellPx,
        hMax * cellPx,
      );
      ctx.restore();
    }
    // Rect-tool drag preview (coords in global tile space, draw region-local).
    if (editMode === "paint" && paintTool === "rect" && dragStart && dragCurrent) {
      const x0 = Math.max(regionX0, Math.min(dragStart.x, dragCurrent.x));
      const y0 = Math.max(regionY0, Math.min(dragStart.y, dragCurrent.y));
      const x1 = Math.min(xMax - 1, Math.max(dragStart.x, dragCurrent.x));
      const y1 = Math.min(yMax - 1, Math.max(dragStart.y, dragCurrent.y));
      if (x1 >= x0 && y1 >= y0) {
        ctx.fillStyle = TERRAIN_COLOR[brush] + "80"; // 50% alpha
        ctx.fillRect(
          (x0 - regionX0) * cellPx,
          (y0 - regionY0) * cellPx,
          (x1 - x0 + 1) * cellPx,
          (y1 - y0 + 1) * cellPx,
        );
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 2;
        ctx.strokeRect(
          (x0 - regionX0) * cellPx + 1,
          (y0 - regionY0) * cellPx + 1,
          (x1 - x0 + 1) * cellPx - 2,
          (y1 - y0 + 1) * cellPx - 2,
        );
      }
    }
  }

  function drawDot(
    ctx: CanvasRenderingContext2D,
    tx: number,
    ty: number,
    color: string,
    label: string,
  ): void {
    // tx/ty are GLOBAL tile coords; canvas is region-local.
    const cx = (tx - regionX0) * cellPx + cellPx / 2;
    const cy = (ty - regionY0) * cellPx + cellPx / 2;
    const r = Math.max(3, cellPx * 0.32);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.8)";
    ctx.lineWidth = 1;
    ctx.stroke();
    if (cellPx >= 16) {
      ctx.fillStyle = "rgba(0,0,0,0.85)";
      ctx.fillRect(cx + r + 1, cy - 7, label.length * 6 + 4, 12);
      ctx.fillStyle = "#fff";
      ctx.font = "10px monospace";
      ctx.textBaseline = "middle";
      ctx.fillText(label, cx + r + 3, cy);
    }
  }

  // ---- Entity click handling --------------------------------------------

  function nextSpawnIndex(z: DraftZone): number {
    const used = new Set(z.spawnPoints.map((s) => s.index));
    for (let i = 0; i < 32; i++) if (!used.has(i)) return i;
    return 0; // schema caps at 32; UI should also surface this
  }

  function handleEntityClick(z: DraftZone, t: { x: number; y: number }): void {
    switch (editMode) {
      case "place_sp": {
        if (z.spawnPoints.length >= 16) {
          alert("Spawn point cap reached (16).");
          return;
        }
        z.spawnPoints.push({ x: t.x, y: t.y, index: nextSpawnIndex(z) });
        markDirty(z);
        return;
      }
      case "place_portal": {
        const otherZones = drafts.filter((d) => d.id !== z.id);
        const targetZone = otherZones[0];
        if (!targetZone) {
          alert("Need at least 2 zones to place a portal. Create another zone first.");
          return;
        }
        if (z.portals.length >= 32) {
          alert("Portal cap reached (32).");
          return;
        }
        z.portals.push({
          x: t.x,
          y: t.y,
          targetZoneId: targetZone.id,
          targetSpawnIndex: targetZone.spawnPoints[0]?.index ?? 0,
        });
        markDirty(z);
        return;
      }
      case "place_mob": {
        const known = cb.getKnownMobIds();
        if (known.length === 0) {
          alert("No mobs in catalogue. Define some in the Mobs tab first.");
          return;
        }
        if (z.mobSpawns.length >= 200) {
          alert("Mob spawn cap reached (200).");
          return;
        }
        z.mobSpawns.push({ x: t.x, y: t.y, templateId: known[0]! });
        markDirty(z);
        return;
      }
      case "place_npc": {
        const known = cb.getKnownNpcIds();
        if (known.length === 0) {
          alert("No NPCs in catalogue. Define some in the NPCs tab first.");
          return;
        }
        if (z.npcSpawns.length >= 64) {
          alert("NPC spawn cap reached (64).");
          return;
        }
        z.npcSpawns.push({ x: t.x, y: t.y, templateId: known[0]!, facing: "S" });
        markDirty(z);
        return;
      }
      case "erase": {
        // Find topmost entity at (t.x, t.y). Order: NPC > mob > portal > SP
        // (matches the visual stacking order of the overlays).
        for (let i = z.npcSpawns.length - 1; i >= 0; i--) {
          if (z.npcSpawns[i]!.x === t.x && z.npcSpawns[i]!.y === t.y) {
            z.npcSpawns.splice(i, 1);
            markDirty(z);
            return;
          }
        }
        for (let i = z.mobSpawns.length - 1; i >= 0; i--) {
          if (z.mobSpawns[i]!.x === t.x && z.mobSpawns[i]!.y === t.y) {
            z.mobSpawns.splice(i, 1);
            markDirty(z);
            return;
          }
        }
        for (let i = z.portals.length - 1; i >= 0; i--) {
          if (z.portals[i]!.x === t.x && z.portals[i]!.y === t.y) {
            z.portals.splice(i, 1);
            markDirty(z);
            return;
          }
        }
        for (let i = z.spawnPoints.length - 1; i >= 0; i--) {
          if (z.spawnPoints[i]!.x === t.x && z.spawnPoints[i]!.y === t.y) {
            // Always keep at least one spawn point (Zod min(1)).
            if (z.spawnPoints.length <= 1) {
              alert("Cannot erase the last spawn point.");
              return;
            }
            z.spawnPoints.splice(i, 1);
            markDirty(z);
            return;
          }
        }
        return;
      }
    }
  }

  // ---- Paste ------------------------------------------------------------

  function pasteClipboardAt(z: DraftZone, x: number, y: number): void {
    if (!clipboard) return;
    // Clip writes that would spill past the zone boundary. Top-left
    // anchor stays at (x, y); we just stop writing when we hit the edge.
    const wMax = Math.min(clipboard.width, z.width - x);
    const hMax = Math.min(clipboard.height, z.height - y);
    if (wMax <= 0 || hMax <= 0) return;
    for (let dy = 0; dy < hMax; dy++) {
      for (let dx = 0; dx < wMax; dx++) {
        z.terrain[ix(z.width, x + dx, y + dy)] =
          clipboard.terrain[dy * clipboard.width + dx] ?? "grass";
      }
    }
    markDirty(z);
  }

  // ---- Iso preview -------------------------------------------------------

  let isoCanvas: HTMLCanvasElement | null = null;

  function renderIsoPreview(z: DraftZone): HTMLElement {
    isoCanvas = document.createElement("canvas");
    isoCanvas.className = "zone-iso";
    renderIsoCanvas(z);
    return el(
      "div",
      { class: "zone-iso-wrap" },
      el("h3", { class: "zone-side-title" }, "Iso preview"),
      isoCanvas,
    );
  }

  function renderIsoCanvas(z: DraftZone): void {
    if (!isoCanvas) return;
    const PREVIEW_W = 320;
    // M11 — render only the visible REGION in iso (the mini-map handles
    // the whole-zone overview). Keeps the iso draw cost ≤ 128² regardless
    // of zone size.
    const { w: rw, h: rh } = regionDims(z);
    const xMax = Math.min(regionX0 + rw, z.width);
    const yMax = Math.min(regionY0 + rh, z.height);
    const isoTileW = Math.min(16, Math.max(4, (PREVIEW_W * 2) / (rw + rh)));
    const isoTileH = isoTileW * (TILE_H / TILE_W);
    const w = (rw + rh) * isoTileW / 2;
    const h = (rw + rh) * isoTileH / 2 + isoTileH;
    isoCanvas.width = Math.ceil(w) + 4;
    isoCanvas.height = Math.ceil(h) + 4;
    isoCanvas.style.width = `${isoCanvas.width}px`;
    isoCanvas.style.height = `${isoCanvas.height}px`;
    const ctx = isoCanvas.getContext("2d")!;
    ctx.clearRect(0, 0, isoCanvas.width, isoCanvas.height);
    const ox = rh * isoTileW / 2 + 2;
    const oy = 2;
    for (let y = regionY0; y < yMax; y++) {
      for (let x = regionX0; x < xMax; x++) {
        const lx = x - regionX0;
        const ly = y - regionY0;
        const sx = ox + (lx - ly) * isoTileW / 2;
        const sy = oy + (lx + ly) * isoTileH / 2;
        const t = z.terrain[ix(z.width, x, y)] ?? "grass";
        ctx.fillStyle = TERRAIN_COLOR[t];
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(sx + isoTileW / 2, sy + isoTileH / 2);
        ctx.lineTo(sx, sy + isoTileH);
        ctx.lineTo(sx - isoTileW / 2, sy + isoTileH / 2);
        ctx.closePath();
        ctx.fill();
      }
    }
    function plot(tx: number, ty: number, color: string): void {
      if (tx < regionX0 || ty < regionY0 || tx >= xMax || ty >= yMax) return;
      const lx = tx - regionX0;
      const ly = ty - regionY0;
      const sx = ox + (lx - ly) * isoTileW / 2;
      const sy = oy + (lx + ly) * isoTileH / 2 + isoTileH / 2;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(sx, sy, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
    if (layerVisible.sp) for (const s of z.spawnPoints) plot(s.x, s.y, "#3ecf6c");
    if (layerVisible.portal) for (const p of z.portals) plot(p.x, p.y, "#4d8be6");
    if (layerVisible.mob) for (const m of z.mobSpawns) plot(m.x, m.y, "#e64d4d");
    if (layerVisible.npc) for (const n of z.npcSpawns) plot(n.x, n.y, "#e6c73a");
  }

  // ---- Mini-map (only shown for zones > REGION_SIZE) --------------------

  let miniCanvas: HTMLCanvasElement | null = null;
  const MINI_PX = 280;

  function renderMiniMap(z: DraftZone): HTMLElement {
    miniCanvas = document.createElement("canvas");
    miniCanvas.className = "zone-minimap";
    drawMiniMap(z);
    // Click + drag → recenters the view on the clicked tile.
    // M14 v2 — behavior splits on subTab:
    //   - construction: pans the region (REGION_PAN_STEP-snapped) — keeps
    //     the existing tile-grid semantic where moves happen by whole
    //     regions for fast surveying.
    //   - decor: sets the iso camera directly on the clicked tile (no
    //     snapping) — matches the right-click-to-center behavior on the
    //     iso canvas for consistency.
    let dragging = false;
    function panTo(ev: MouseEvent): void {
      if (!miniCanvas) return;
      const rect = miniCanvas.getBoundingClientRect();
      const scaleX = z.width / miniCanvas.width;
      const scaleY = z.height / miniCanvas.height;
      const tx = Math.floor((ev.clientX - rect.left) * scaleX);
      const ty = Math.floor((ev.clientY - rect.top) * scaleY);

      if (subTab === "decor") {
        decorCamera = {
          tx: Math.max(0, Math.min(z.width - 1, tx)),
          ty: Math.max(0, Math.min(z.height - 1, ty)),
        };
        renderEditor();
        return;
      }

      const { w: rw, h: rh } = regionDims(z);
      // Snap to REGION_PAN_STEP-tile grid before clamping. Clamping to
      // zone bounds may un-snap on the right/bottom edge — that's OK,
      // it just means the last region butts up against the zone wall.
      const desiredX = tx - Math.floor(rw / 2);
      const desiredY = ty - Math.floor(rh / 2);
      const snappedX = Math.round(desiredX / REGION_PAN_STEP) * REGION_PAN_STEP;
      const snappedY = Math.round(desiredY / REGION_PAN_STEP) * REGION_PAN_STEP;
      regionX0 = Math.max(0, Math.min(z.width - rw, snappedX));
      regionY0 = Math.max(0, Math.min(z.height - rh, snappedY));
      // Re-render the canvas + iso preview + mini-map (region rect moves).
      const main = editorBody.querySelector(".zone-canvas") as HTMLCanvasElement | null;
      if (main) {
        // Canvas size may need to change if region clipped at the edge.
        const { w, h } = regionDims(z);
        const xMaxNew = Math.min(regionX0 + w, z.width);
        const yMaxNew = Math.min(regionY0 + h, z.height);
        main.width = (xMaxNew - regionX0) * cellPx;
        main.height = (yMaxNew - regionY0) * cellPx;
        main.style.width = `${main.width}px`;
        main.style.height = `${main.height}px`;
        drawTopDown(main.getContext("2d")!, z);
      }
      renderIsoCanvas(z);
      drawMiniMap(z);
    }
    miniCanvas.addEventListener("mousedown", (ev) => {
      dragging = true;
      panTo(ev);
      // Attach the mouseup listener fresh inside each mousedown so it
      // self-cleans even if the user releases outside the canvas. The
      // earlier init-time `{once:true}` listener died after the first
      // mouseup and never re-armed → drag stuck on after subsequent
      // clicks.
      const onUp = (): void => {
        dragging = false;
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mouseup", onUp);
    });
    miniCanvas.addEventListener("mousemove", (ev) => {
      if (dragging) panTo(ev);
    });
    return el(
      "div",
      { class: "zone-iso-wrap" },
      el(
        "h3",
        { class: "zone-side-title" },
        `World map (${z.width}×${z.height} — click to pan)`,
      ),
      miniCanvas,
    );
  }

  function drawMiniMap(z: DraftZone): void {
    if (!miniCanvas) return;
    const aspect = z.width / z.height;
    let w: number, h: number;
    if (aspect >= 1) {
      w = MINI_PX;
      h = Math.round(MINI_PX / aspect);
    } else {
      h = MINI_PX;
      w = Math.round(MINI_PX * aspect);
    }
    miniCanvas.width = w;
    miniCanvas.height = h;
    miniCanvas.style.width = `${w}px`;
    miniCanvas.style.height = `${h}px`;
    const ctx = miniCanvas.getContext("2d")!;
    // M11 — sample-based rendering: 1 px per (zone/mini) ratio. For a
    // 1024² zone into 280 px, that's 1 px per ~3.6 tiles, so we step by
    // floor(zone/mini) and draw 1×1 px per sampled tile. Avoids the
    // 1M-iteration cost of a per-tile loop.
    const stepX = Math.max(1, Math.floor(z.width / w));
    const stepY = Math.max(1, Math.floor(z.height / h));
    const pxX = w / Math.ceil(z.width / stepX);
    const pxY = h / Math.ceil(z.height / stepY);
    for (let py = 0; py * stepY < z.height; py++) {
      for (let px = 0; px * stepX < z.width; px++) {
        const tx = px * stepX;
        const ty = py * stepY;
        const t = z.terrain[ix(z.width, tx, ty)] ?? "grass";
        ctx.fillStyle = TERRAIN_COLOR[t];
        ctx.fillRect(px * pxX, py * pxY, Math.ceil(pxX), Math.ceil(pxY));
      }
    }
    // Region rectangle.
    const { w: rw, h: rh } = regionDims(z);
    const rx = (regionX0 / z.width) * w;
    const ry = (regionY0 / z.height) * h;
    const rw2 = (rw / z.width) * w;
    const rh2 = (rh / z.height) * h;
    ctx.strokeStyle = "#f5cc66";
    ctx.lineWidth = 2;
    ctx.strokeRect(rx, ry, rw2, rh2);
    ctx.fillStyle = "rgba(245, 204, 102, 0.15)";
    ctx.fillRect(rx, ry, rw2, rh2);
  }

  // ---- Side entity panel (lists with form controls) ---------------------

  function renderEntityPanel(z: DraftZone): HTMLElement {
    return el(
      "div",
      { class: "zone-entity-panel" },
      renderSpawnList(z),
      renderPortalList(z),
      renderMobList(z),
      renderNpcList(z),
    );
  }

  function refreshSidePanel(z: DraftZone): void {
    const fresh = renderEntityPanel(z);
    const old = editorBody.querySelector(".zone-entity-panel");
    if (old && old.parentElement) old.parentElement.replaceChild(fresh, old);
    renderIsoCanvas(z);
    if (isRegionMode(z)) drawMiniMap(z);
    const main = editorBody.querySelector(".zone-canvas") as HTMLCanvasElement | null;
    if (main) drawTopDown(main.getContext("2d")!, z);
  }

  function renderSpawnList(z: DraftZone): HTMLElement {
    const rows = z.spawnPoints.map((s, i) =>
      el(
        "div",
        { class: "zone-entity-row" },
        intInput(s.index, 0, 31, (v) => {
          // Reject duplicate index.
          if (z.spawnPoints.some((other, j) => j !== i && other.index === v)) return;
          z.spawnPoints[i]!.index = v;
          markDirty(z);
          refreshSidePanel(z);
        }, "idx"),
        intInput(s.x, 0, z.width - 1, (v) => {
          z.spawnPoints[i]!.x = v;
          markDirty(z);
          refreshSidePanel(z);
        }, "x"),
        intInput(s.y, 0, z.height - 1, (v) => {
          z.spawnPoints[i]!.y = v;
          markDirty(z);
          refreshSidePanel(z);
        }, "y"),
        deleteRowBtn(() => {
          if (z.spawnPoints.length <= 1) {
            alert("Need at least one spawn point.");
            return;
          }
          z.spawnPoints.splice(i, 1);
          markDirty(z);
          refreshSidePanel(z);
        }),
      ),
    );
    return el(
      "div",
      { class: "zone-entity-section" },
      el("h4", { class: "zone-section-title" }, "Spawn points ", el("span", { class: "muted" }, `(${z.spawnPoints.length}/16)`)),
      ...rows,
    );
  }

  function renderPortalList(z: DraftZone): HTMLElement {
    const allZoneIds = drafts.map((d) => d.id).filter((id) => id !== z.id);
    const rows = z.portals.map((p, i) => {
      const targetZone = drafts.find((d) => d.id === p.targetZoneId);
      const targetIndices = targetZone
        ? targetZone.spawnPoints.map((s) => String(s.index))
        : [];
      const zoneCombo = createCombobox({
        options: allZoneIds,
        value: p.targetZoneId,
        placeholder: "Target zone",
        onChange: (v) => {
          z.portals[i]!.targetZoneId = v;
          markDirty(z);
          refreshSidePanel(z);
        },
      });
      return el(
        "div",
        { class: "zone-entity-row" },
        intInput(p.x, 0, z.width - 1, (v) => {
          z.portals[i]!.x = v;
          markDirty(z);
          refreshSidePanel(z);
        }, "x"),
        intInput(p.y, 0, z.height - 1, (v) => {
          z.portals[i]!.y = v;
          markDirty(z);
          refreshSidePanel(z);
        }, "y"),
        zoneCombo,
        targetIndices.length > 0
          ? selectField(targetIndices, String(p.targetSpawnIndex), (v) => {
              z.portals[i]!.targetSpawnIndex = Number.parseInt(v, 10) || 0;
              markDirty(z);
              refreshSidePanel(z);
            })
          : el("span", { class: "stale" }, "(no SP)"),
        deleteRowBtn(() => {
          z.portals.splice(i, 1);
          markDirty(z);
          refreshSidePanel(z);
        }),
      );
    });
    return el(
      "div",
      { class: "zone-entity-section" },
      el("h4", { class: "zone-section-title" }, "Portals ", el("span", { class: "muted" }, `(${z.portals.length}/32)`)),
      ...rows,
    );
  }

  function renderMobList(z: DraftZone): HTMLElement {
    const known = cb.getKnownMobIds();
    const rows = z.mobSpawns.map((m, i) => {
      const combo = createCombobox({
        options: known,
        value: m.templateId,
        placeholder: "Mob template id",
        onChange: (v) => {
          z.mobSpawns[i]!.templateId = v;
          markDirty(z);
          refreshSidePanel(z);
        },
      });
      return el(
        "div",
        { class: "zone-entity-row" },
        intInput(m.x, 0, z.width - 1, (v) => {
          z.mobSpawns[i]!.x = v;
          markDirty(z);
          refreshSidePanel(z);
        }, "x"),
        intInput(m.y, 0, z.height - 1, (v) => {
          z.mobSpawns[i]!.y = v;
          markDirty(z);
          refreshSidePanel(z);
        }, "y"),
        combo,
        deleteRowBtn(() => {
          z.mobSpawns.splice(i, 1);
          markDirty(z);
          refreshSidePanel(z);
        }),
      );
    });
    return el(
      "div",
      { class: "zone-entity-section" },
      el("h4", { class: "zone-section-title" }, "Mob spawns ", el("span", { class: "muted" }, `(${z.mobSpawns.length}/200)`)),
      ...rows,
    );
  }

  function renderNpcList(z: DraftZone): HTMLElement {
    const known = cb.getKnownNpcIds();
    const rows = z.npcSpawns.map((n, i) => {
      const combo = createCombobox({
        options: known,
        value: n.templateId,
        placeholder: "NPC template id",
        onChange: (v) => {
          z.npcSpawns[i]!.templateId = v;
          markDirty(z);
          refreshSidePanel(z);
        },
      });
      return el(
        "div",
        { class: "zone-entity-row" },
        intInput(n.x, 0, z.width - 1, (v) => {
          z.npcSpawns[i]!.x = v;
          markDirty(z);
          refreshSidePanel(z);
        }, "x"),
        intInput(n.y, 0, z.height - 1, (v) => {
          z.npcSpawns[i]!.y = v;
          markDirty(z);
          refreshSidePanel(z);
        }, "y"),
        combo,
        selectField(["N", "S", "E", "W"], n.facing, (v) => {
          z.npcSpawns[i]!.facing = v as typeof n.facing;
          markDirty(z);
          refreshSidePanel(z);
        }),
        deleteRowBtn(() => {
          z.npcSpawns.splice(i, 1);
          markDirty(z);
          refreshSidePanel(z);
        }),
      );
    });
    return el(
      "div",
      { class: "zone-entity-section" },
      el("h4", { class: "zone-section-title" }, "NPC spawns ", el("span", { class: "muted" }, `(${z.npcSpawns.length}/64)`)),
      ...rows,
    );
  }

  // ---- DOM helper widgets -----------------------------------------------

  function textField(label: string, value: string, onChange: (v: string) => void): HTMLElement {
    return el(
      "div",
      { class: "field" },
      el("label", {}, label),
      el("input", {
        type: "text",
        value,
        oninput: (e: Event) => onChange((e.target as HTMLInputElement).value),
      }),
    );
  }

  function intField(
    label: string,
    value: number,
    min: number,
    max: number,
    onChange: (v: number) => void,
  ): HTMLElement {
    return el(
      "div",
      { class: "field" },
      el("label", {}, label),
      el("input", {
        type: "number",
        value: String(value),
        min: String(min),
        max: String(max),
        step: "1",
        onchange: (e: Event) => {
          const v = Number.parseInt((e.target as HTMLInputElement).value, 10);
          if (Number.isFinite(v)) onChange(Math.max(min, Math.min(max, v)));
        },
      }),
    );
  }

  function checkboxField(label: string, value: boolean, onChange: (v: boolean) => void): HTMLElement {
    return el(
      "div",
      { class: "field" },
      el(
        "label",
        {},
        el("input", {
          type: "checkbox",
          checked: value,
          onchange: (e: Event) => onChange((e.target as HTMLInputElement).checked),
        }),
        " ",
        label,
      ),
    );
  }

  function intInput(
    value: number,
    min: number,
    max: number,
    onChange: (v: number) => void,
    title: string,
  ): HTMLElement {
    return el("input", {
      type: "number",
      class: "zone-int-input",
      value: String(value),
      min: String(min),
      max: String(max),
      step: "1",
      title,
      onchange: (e: Event) => {
        const v = Number.parseInt((e.target as HTMLInputElement).value, 10);
        if (Number.isFinite(v)) onChange(Math.max(min, Math.min(max, v)));
      },
    });
  }

  function selectField(
    options: string[],
    value: string,
    onChange: (v: string) => void,
  ): HTMLElement {
    const opts = options.map((o) =>
      el("option", { value: o, selected: o === value }, o),
    );
    return el(
      "select",
      {
        class: "zone-select",
        onchange: (e: Event) => onChange((e.target as HTMLSelectElement).value),
      },
      ...opts,
    );
  }

  function deleteRowBtn(onClick: () => void): HTMLElement {
    return el(
      "button",
      {
        type: "button",
        class: "zone-row-delete",
        title: "Delete",
        onclick: onClick,
      },
      "×",
    );
  }

  // Global Ctrl+Z / Cmd+Z. We listen on window because the canvas can't
  // hold focus reliably (Pixi/click-to-paint shouldn't steal the
  // keyboard), but we guard against firing while another screen is
  // active (root not in DOM) or while a text input has focus.
  window.addEventListener("keydown", (ev) => {
    if (!(ev.ctrlKey || ev.metaKey) || ev.key !== "z") return;
    if (!document.contains(root)) return;
    const target = ev.target as HTMLElement | null;
    if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
    ev.preventDefault();
    popUndo();
  });

  // M14 v2 — live resize for the decor iso canvas. Window resize fires
  // many events per drag; debounce to 150 ms so we don't thrash. Only
  // re-render in decor mode where canvas dims are computed from window;
  // construction mode uses fixed-size grid canvas + side panel and
  // doesn't need to react. Listener never removed — createZonesScreen
  // lives for the lifetime of the admin SPA, and the render guards
  // against detached state by checking subTab + selectedId.
  let resizeTimer: number | null = null;
  window.addEventListener("resize", () => {
    if (resizeTimer !== null) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      resizeTimer = null;
      if (subTab !== "decor" || selectedId === null) return;
      if (!document.contains(root)) return;
      renderEditor();
    }, 150);
  });

  return {
    root,
    setZones(zones) {
      drafts = zones.map(cloneZone);
      if (selectedId && !drafts.some((d) => d.id === selectedId)) {
        selectedId = null;
      }
      undoStack = [];
      cb.onDirtyChange(false);
      renderList();
      renderEditor();
    },
    getDraft() {
      return drafts.map(sanitize);
    },
    isDirty() {
      return drafts.some((d) => d.__isNew || d.__isDirty);
    },
    markPublished() {
      for (const d of drafts) {
        delete d.__isNew;
        delete d.__isDirty;
      }
      cb.onDirtyChange(false);
      renderList();
    },
  };
}
